/**
 * The sidebar, per portal — and the one place it is defined.
 *
 * The layout renders it for the signed-in user; the plan editor renders it for
 * a plan, to show a Super Admin exactly which menu each role would get. Both
 * filter it the same way (`visibleNav`), so the preview cannot drift from what
 * a school actually sees.
 *
 * An item with a `permission` is shown only to somebody holding it — every one
 * of them, for a list. Items without one are the portal's own home pages.
 */
import {
  LayoutDashboard, Building2, Users, GraduationCap, HeartHandshake, Calendar, Layers, BookOpen, CalendarDays, Megaphone, ClipboardList, Wallet, ClipboardCheck, FileText, Award, ShieldCheck, KeyRound, UserCog, PenLine, ArrowUpRight, ScrollText, ListChecks, School, Plane, MessageSquare, BadgeIndianRupee
} from 'lucide-angular';
import type { LucideIconData } from 'lucide-angular';

import { OFFICE_DASHBOARD_CODES } from '../../core/util/dashboard-access';

export type PortalRole = 'super-admin' | 'school-admin' | 'teacher' | 'parent' | 'student';

export interface NavItem {
  label: string;
  route: string;
  icon: LucideIconData;
  exact?: boolean;
  /** When set, the item is only shown to a user who holds this permission (all of them, for a list). */
  permission?: string | string[];
  /** When set, the item is shown to a user who holds any one of these — a page made of cards, each behind its own permission. */
  anyPermission?: readonly string[];
}

export interface NavGroup {
  heading?: string;
  items: NavItem[];
}

/** The portal a role signs in to. Every office role shares the school-admin one. */
export function portalFor(role: string | null | undefined): PortalRole {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'super-admin';
    case 'TEACHER':
      return 'teacher';
    case 'PARENT':
      return 'parent';
    case 'STUDENT':
      return 'student';
    default:
      return 'school-admin';
  }
}

export const NAV_BY_ROLE: Record<PortalRole, NavGroup[]> = {
  'super-admin': [
    {
      items: [
        { label: 'nav.dashboard', route: '/dashboard', icon: LayoutDashboard, exact: true },
        { label: 'nav.schools', route: '/schools', icon: Building2 },
        { label: 'nav.parents', route: '/parents', icon: HeartHandshake, exact: true },
        { label: 'nav.plans', route: '/plans', icon: BadgeIndianRupee, permission: 'plan.manage' }
      ]
    },
    {
      heading: 'navGroup.governance', items: [
        { label: 'nav.auditLogs', route: '/audit-logs', icon: ScrollText }
      ]
    }
  ],
  'school-admin': [
    {
      items: [
        { label: 'nav.dashboard', route: '/school-admin/dashboard', icon: LayoutDashboard, anyPermission: OFFICE_DASHBOARD_CODES }
      ]
    },
    // Most of this group is the school-wide admin's; a branch admin sees
    // only the privileges and staff-account screens, office staff none of it.
    {
      heading: 'navGroup.school', items: [
        { label: 'nav.schoolDetails', route: '/school-admin/school', icon: School, permission: 'school.update' },
        { label: 'nav.branches', route: '/school-admin/branches', icon: Building2, permission: 'branch.create' },
        { label: 'nav.administrators', route: '/school-admin/administrators', icon: ShieldCheck, permission: 'branch.create' },
        { label: 'nav.rolePrivileges', route: '/school-admin/role-privileges', icon: KeyRound, permission: 'privilege.manage' },
        { label: 'nav.staffAccounts', route: '/school-admin/staff', icon: UserCog, permission: 'user.provision' },
        { label: 'nav.auditLog', route: '/school-admin/audit-logs', icon: ScrollText, permission: 'audit.view' }
      ]
    },
    {
      heading: 'navGroup.academicSetup', items: [
        { label: 'nav.academicYears', route: '/school-admin/academic-years', icon: Calendar, permission: 'academic_year.read' },
        { label: 'nav.classesSections', route: '/school-admin/classes', icon: Layers, permission: 'class.read' },
        { label: 'nav.subjects', route: '/school-admin/subjects', icon: BookOpen, permission: 'subject.read' },
        { label: 'nav.teachingAssignments', route: '/school-admin/teaching-assignments', icon: ClipboardList, permission: ['class.read', 'subject.read', 'teacher.read'] }
      ]
    },
    {
      heading: 'navGroup.people', items: [
        { label: 'nav.teachers', route: '/school-admin/teachers', icon: Users, permission: 'teacher.read' },
        { label: 'nav.students', route: '/school-admin/students', icon: GraduationCap, permission: 'student.read' }
      ]
    },
    {
      heading: 'navGroup.operations', items: [
        { label: 'nav.promotion', route: '/school-admin/promotion', icon: ArrowUpRight, permission: 'enrollment.write' },
        { label: 'nav.attendance', route: '/school-admin/attendance', icon: ClipboardCheck, permission: 'attendance.view' },
        { label: 'nav.staffTasks', route: '/tasks', icon: ListChecks, permission: 'task.view' },
        { label: 'nav.exams', route: '/school-admin/exams', icon: FileText, permission: 'exam.read' },
        { label: 'nav.marksEntry', route: '/school-admin/marks-entry', icon: PenLine, permission: 'marks.enter' },
        { label: 'nav.results', route: '/school-admin/results', icon: Award, permission: 'result.view' },
        { label: 'nav.timetable', route: '/school-admin/timetable', icon: CalendarDays, permission: 'timetable.read' },
        { label: 'nav.fees', route: '/school-admin/fees', icon: Wallet, permission: 'fee.read' }
      ]
    },
    {
      heading: 'navGroup.communication', items: [
        { label: 'nav.noticeBoard', route: '/announcements', icon: Megaphone, exact: true, permission: 'announcement.read' },
        { label: 'nav.messaging', route: '/school-admin/messaging', icon: MessageSquare, permission: 'messaging.read' }
      ]
    }
  ],
  teacher: [
    {
      items: [
        { label: 'nav.dashboard', route: '/teacher/dashboard', icon: LayoutDashboard },
        { label: 'nav.myStudents', route: '/teacher/students', icon: GraduationCap, permission: 'student.read' },
        { label: 'nav.attendance', route: '/teacher/attendance', icon: ClipboardCheck, permission: 'attendance.record' },
        { label: 'nav.marksEntry', route: '/teacher/marks-entry', icon: FileText, permission: 'marks.enter' },
        { label: 'nav.assignments', route: '/teacher/assignments', icon: ClipboardList, permission: 'assignment.read' },
        { label: 'nav.myTasks', route: '/tasks', icon: ListChecks, permission: 'task.view' },
        { label: 'nav.myTimetable', route: '/timetable', icon: CalendarDays, permission: 'timetable.read' },
        { label: 'nav.calendar', route: '/calendar', icon: Calendar, permission: 'calendar.read' },
        { label: 'nav.leave', route: '/leave', icon: Plane, permission: 'leave.request' }
      ]
    },
    {
      heading: 'navGroup.communication', items: [
        { label: 'nav.noticeBoard', route: '/announcements', icon: Megaphone, exact: true, permission: 'announcement.read' }
      ]
    }
  ],
  parent: [
    {
      items: [
        { label: 'nav.dashboard', route: '/parent/dashboard', icon: LayoutDashboard },
        { label: 'nav.academicRecord', route: '/parent/child', icon: Award },
        { label: 'nav.homework', route: '/parent/assignments', icon: ClipboardList, permission: 'assignment.read' },
        { label: 'nav.exams', route: '/exams', icon: FileText, permission: 'self.exam' },
        { label: 'nav.timetable', route: '/timetable', icon: CalendarDays, permission: 'self.timetable' },
        { label: 'nav.feesPayments', route: '/parent/fees', icon: Wallet, permission: 'self.fees' }
      ]
    },
    {
      heading: 'navGroup.communication', items: [
        { label: 'nav.noticeBoard', route: '/announcements', icon: Megaphone, exact: true, permission: 'announcement.read' },
        { label: 'nav.messagesFromSchool', route: '/parent/messages', icon: MessageSquare, permission: 'children.view' }
      ]
    }
  ],
  student: [
    {
      items: [
        { label: 'nav.dashboard', route: '/student/dashboard', icon: LayoutDashboard },
        { label: 'nav.myRecord', route: '/student/record', icon: Award },
        { label: 'nav.myExams', route: '/exams', icon: FileText, permission: 'self.exam' },
        { label: 'nav.myAssignments', route: '/student/assignments', icon: ClipboardList, permission: 'assignment.read' },
        { label: 'nav.myTimetable', route: '/timetable', icon: CalendarDays, permission: 'self.timetable' },
        { label: 'nav.calendar', route: '/calendar', icon: Calendar, permission: 'calendar.read' }
      ]
    },
    {
      heading: 'navGroup.communication', items: [
        { label: 'nav.noticeBoard', route: '/announcements', icon: Megaphone, exact: true, permission: 'announcement.read' }
      ]
    }
  ]
};

/** The groups `portal` shows to somebody for whom `can(code)` answers. */
export function visibleNav(portal: PortalRole, can: (code: string) => boolean): NavGroup[] {
  return NAV_BY_ROLE[portal]
    .map(group => ({
      ...group,
      items: group.items.filter(item =>
        (!item.permission || [item.permission].flat().every(code => can(code))) &&
        (!item.anyPermission || item.anyPermission.some(code => can(code))))
    }))
    .filter(group => group.items.length > 0);
}
